import { useState, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Search, Info, GripVertical, X } from "lucide-react";
import { toast } from "sonner";

export interface ColumnDefinition {
  id: string;
  name: string;
  description: string;
}

export interface ColumnConfig {
  visibleColumns: string[];
  columnOrder: string[];
}

interface ConfigureColumnsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnDefinition[];
  currentConfig: ColumnConfig;
  onSave: (config: ColumnConfig) => void;
  defaultConfig: ColumnConfig;
}

export function ConfigureColumnsModal({
  open,
  onOpenChange,
  columns,
  currentConfig,
  onSave,
  defaultConfig,
}: ConfigureColumnsModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragItemRef = useRef<HTMLDivElement | null>(null);

  // Initialize state when modal opens
  useEffect(() => {
    if (open) {
      setVisibleColumns([...currentConfig.visibleColumns]);
      setColumnOrder([...currentConfig.columnOrder]);
      setSearchQuery("");
    }
  }, [open, currentConfig]);

  // Filter columns based on search query
  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) {
      return columns;
    }
    const query = searchQuery.toLowerCase();
    return columns.filter(
      (col) =>
        col.name.toLowerCase().includes(query) ||
        col.description.toLowerCase().includes(query)
    );
  }, [columns, searchQuery]);

  // Sort filtered columns alphabetically
  const sortedAvailableColumns = useMemo(() => {
    return [...filteredColumns].sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredColumns]);

  // Get visible columns in order
  const orderedVisibleColumns = useMemo(() => {
    return columnOrder
      .filter((id) => visibleColumns.includes(id))
      .map((id) => columns.find((col) => col.id === id))
      .filter((col): col is ColumnDefinition => col !== undefined);
  }, [columnOrder, visibleColumns, columns]);

  // Handle checkbox toggle in available columns
  const handleToggleColumn = (columnId: string, checked: boolean) => {
    if (checked) {
      // Add column to visible columns
      setVisibleColumns((prev) => [...prev, columnId]);
      // Add to end of order if not already there
      if (!columnOrder.includes(columnId)) {
        setColumnOrder((prev) => [...prev, columnId]);
      }
      // Announce to screen readers
      const column = columns.find((col) => col.id === columnId);
      if (column) {
        setTimeout(() => {
          const announcement = `${column.name} added to visible columns`;
          const liveRegion = document.getElementById("column-announcements");
          if (liveRegion) {
            liveRegion.textContent = announcement;
          }
        }, 100);
      }
    } else {
      // Check minimum columns constraint
      if (visibleColumns.length <= 3) {
        toast.error("Cannot remove column. Minimum 3 columns must be visible");
        // Announce to screen readers
        setTimeout(() => {
          const liveRegion = document.getElementById("column-announcements");
          if (liveRegion) {
            liveRegion.textContent =
              "Cannot remove column. Minimum 3 columns must be visible";
          }
        }, 100);
        return;
      }
      // Remove column from visible columns
      setVisibleColumns((prev) => prev.filter((id) => id !== columnId));
      // Announce to screen readers
      const column = columns.find((col) => col.id === columnId);
      if (column) {
        setTimeout(() => {
          const announcement = `${column.name} removed from visible columns`;
          const liveRegion = document.getElementById("column-announcements");
          if (liveRegion) {
            liveRegion.textContent = announcement;
          }
        }, 100);
      }
    }
  };

  // Handle remove button in visible columns
  const handleRemoveColumn = (columnId: string) => {
    handleToggleColumn(columnId, false);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
    // Announce to screen readers
    const column = orderedVisibleColumns[index];
    if (column) {
      setTimeout(() => {
        const liveRegion = document.getElementById("column-announcements");
        if (liveRegion) {
          liveRegion.textContent = `Picked up ${column.name}`;
        }
      }, 100);
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newOrder = [...columnOrder];
    const draggedColumnId = orderedVisibleColumns[draggedIndex].id;
    const dropColumnId = orderedVisibleColumns[dropIndex].id;

    // Find positions in the full order array
    const draggedPos = newOrder.indexOf(draggedColumnId);
    const dropPos = newOrder.indexOf(dropColumnId);

    // Remove from old position
    newOrder.splice(draggedPos, 1);
    // Insert at new position
    const newDropPos = newOrder.indexOf(dropColumnId);
    newOrder.splice(
      draggedIndex < dropIndex ? newDropPos + 1 : newDropPos,
      0,
      draggedColumnId
    );

    setColumnOrder(newOrder);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Announce to screen readers
    const column = columns.find((col) => col.id === draggedColumnId);
    if (column) {
      setTimeout(() => {
        const liveRegion = document.getElementById("column-announcements");
        if (liveRegion) {
          liveRegion.textContent = `Moved ${column.name} to position ${dropIndex + 1}`;
        }
      }, 100);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Keyboard navigation for drag and drop
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      if (draggedIndex === null) {
        // Pick up item
        setDraggedIndex(index);
        const column = orderedVisibleColumns[index];
        if (column) {
          toast.info(`Picked up ${column.name}. Use arrow keys to move.`);
        }
      } else if (draggedIndex === index) {
        // Drop item at current position
        setDraggedIndex(null);
        const column = orderedVisibleColumns[index];
        if (column) {
          toast.success(`Dropped ${column.name}`);
        }
      }
    } else if (e.key === "Escape") {
      if (draggedIndex !== null) {
        setDraggedIndex(null);
        toast.info("Drag cancelled");
      }
    } else if (draggedIndex !== null && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      const direction = e.key === "ArrowUp" ? -1 : 1;
      const newIndex = draggedIndex + direction;

      if (newIndex >= 0 && newIndex < orderedVisibleColumns.length) {
        // Swap positions
        const newOrder = [...columnOrder];
        const draggedColumnId = orderedVisibleColumns[draggedIndex].id;
        const targetColumnId = orderedVisibleColumns[newIndex].id;

        const draggedPos = newOrder.indexOf(draggedColumnId);
        const targetPos = newOrder.indexOf(targetColumnId);

        // Swap
        [newOrder[draggedPos], newOrder[targetPos]] = [
          newOrder[targetPos],
          newOrder[draggedPos],
        ];

        setColumnOrder(newOrder);
        setDraggedIndex(newIndex);

        const column = columns.find((col) => col.id === draggedColumnId);
        if (column) {
          toast.info(`Moved ${column.name} to position ${newIndex + 1}`);
        }
      }
    }
  };

  // Handle reset to default
  const handleResetToDefault = () => {
    setIsResetDialogOpen(true);
  };

  const confirmReset = () => {
    setVisibleColumns([...defaultConfig.visibleColumns]);
    setColumnOrder([...defaultConfig.columnOrder]);
    setIsResetDialogOpen(false);
    toast.success("Configuration reset to default");
  };

  // Handle save
  const handleSave = () => {
    // Validate minimum columns
    if (visibleColumns.length < 3) {
      toast.error("Minimum 3 columns must be visible");
      return;
    }

    // Save configuration
    onSave({
      visibleColumns: [...visibleColumns],
      columnOrder: [...columnOrder],
    });

    // Save to localStorage
    localStorage.setItem(
      "stockItemsColumnConfig",
      JSON.stringify({
        visibleColumns: [...visibleColumns],
        columnOrder: [...columnOrder],
      })
    );

    toast.success("Column configuration saved");
    onOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[600px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle>Configure Columns</DialogTitle>
            <DialogDescription>
              Choose which columns to display in the table
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden px-6">
            <div className="grid grid-cols-2 gap-4 h-full">
              {/* Left Panel: Available Columns */}
              <div className="flex flex-col gap-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm">Available Columns</h3>
                  </div>
                  <Input
                    placeholder="Search columns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9"
                  />
                </div>

                <Card className="flex-1 overflow-hidden">
                  <ScrollArea className="h-[400px]">
                    <div className="p-3 space-y-1">
                      {sortedAvailableColumns.length > 0 ? (
                        sortedAvailableColumns.map((column) => (
                          <div
                            key={column.id}
                            className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 transition-colors"
                          >
                            <Checkbox
                              id={`available-${column.id}`}
                              checked={visibleColumns.includes(column.id)}
                              onCheckedChange={(checked) =>
                                handleToggleColumn(column.id, checked as boolean)
                              }
                              aria-label={`Toggle ${column.name}`}
                            />
                            <label
                              htmlFor={`available-${column.id}`}
                              className="flex-1 text-sm cursor-pointer"
                            >
                              {column.name}
                            </label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                    aria-label={`Info about ${column.name}`}
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-[200px]">{column.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                          No columns found
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </Card>
              </div>

              {/* Right Panel: Visible Columns */}
              <div className="flex flex-col gap-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm">Visible Columns</h3>
                    <Badge variant="secondary" className="text-xs">
                      {visibleColumns.length}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">Drag to reorder</p>
                </div>

                <Card className="flex-1 overflow-hidden bg-muted/30">
                  <ScrollArea className="h-[400px]">
                    <div className="p-3 space-y-1">
                      {orderedVisibleColumns.map((column, index) => (
                        <div
                          key={column.id}
                          ref={draggedIndex === index ? dragItemRef : null}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          tabIndex={0}
                          className={`flex items-center gap-2 p-2 rounded bg-background border transition-all ${
                            draggedIndex === index
                              ? "opacity-50 scale-95"
                              : "opacity-100 scale-100"
                          } ${
                            dragOverIndex === index && draggedIndex !== index
                              ? "border-primary border-2"
                              : "border-border"
                          } cursor-move hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2`}
                          role="button"
                          aria-label={`${column.name}, position ${index + 1} of ${orderedVisibleColumns.length}. Press space to pick up, arrow keys to move, space to drop.`}
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <Checkbox
                            checked={true}
                            onCheckedChange={() => handleRemoveColumn(column.id)}
                            aria-label={`Remove ${column.name}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="flex-1 text-sm">{column.name}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveColumn(column.id);
                            }}
                            className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                            aria-label={`Remove ${column.name}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </Card>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t flex-row justify-between items-center">
            <Button variant="outline" onClick={handleResetToDefault}>
              Reset to Default
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save Configuration</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to default columns?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear your current configuration and restore the default
              columns.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReset}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Screen reader announcements */}
      <div
        id="column-announcements"
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />
    </>
  );
}
